/**
 * Utility functions for handling post-authentication redirects
 * Implements security measures to prevent open redirect vulnerabilities
 */

// Valid redirect paths that are allowed after authentication
const VALID_REDIRECT_PATHS = [
  '/dashboard',
  '/profile',
  '/profile-setup',
  '/campaigns',
  '/campaigns/create',
  '/discover',
  '/search',
  '/matches',
  '/messages',
];

// Regex patterns for dynamic routes
const VALID_DYNAMIC_PATTERNS = [
  /^\/campaigns\/[a-zA-Z0-9-]+$/, // Campaign details
  /^\/influencers\/[a-zA-Z0-9-]+$/, // Influencer profile
];

// Session storage keys
const REDIRECT_PATH_KEY = 'auth_redirect_path';
const INTENDED_ACTION_KEY = 'auth_intended_action';
const REDIRECT_TIMESTAMP_KEY = 'auth_redirect_timestamp';

// Redirect data expires after 30 minutes
const REDIRECT_EXPIRY_MS = 30 * 60 * 1000;

/**
 * Validates if a redirect path is safe and allowed
 */
export function isValidRedirectPath(path: string): boolean {
  if (!path || typeof path !== 'string') {
    return false;
  }

  // Prevent absolute URLs or protocol-relative URLs (open redirect vulnerability)
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('//')) {
    return false;
  }

  // Must start with /
  if (!path.startsWith('/')) {
    return false;
  }

  // Check against static paths
  if (VALID_REDIRECT_PATHS.includes(path)) {
    return true;
  }

  // Check against dynamic patterns
  return VALID_DYNAMIC_PATTERNS.some(pattern => pattern.test(path));
}

/**
 * Stores the intended redirect path and action in session storage
 */
export function storeRedirectIntent(redirectPath: string, action?: string): void {
  if (!isValidRedirectPath(redirectPath)) {
    console.warn('Invalid redirect path, not storing:', redirectPath);
    return;
  }

  try {
    sessionStorage.setItem(REDIRECT_PATH_KEY, redirectPath);
    sessionStorage.setItem(REDIRECT_TIMESTAMP_KEY, Date.now().toString());
    
    if (action) {
      sessionStorage.setItem(INTENDED_ACTION_KEY, action);
    }
  } catch (error) {
    console.error('Failed to store redirect intent:', error);
  }
}

/**
 * Retrieves the stored redirect intent if it exists and hasn't expired
 */
export function getRedirectIntent(): { path: string; action?: string } | null {
  try {
    const path = sessionStorage.getItem(REDIRECT_PATH_KEY);
    const timestamp = sessionStorage.getItem(REDIRECT_TIMESTAMP_KEY);
    const action = sessionStorage.getItem(INTENDED_ACTION_KEY);

    // No redirect stored
    if (!path) {
      return null;
    }

    // Check if redirect has expired
    if (timestamp) {
      const age = Date.now() - parseInt(timestamp, 10);
      if (age > REDIRECT_EXPIRY_MS) {
        console.log('Redirect intent expired, clearing');
        clearRedirectIntent();
        return null;
      }
    }

    // Validate the stored path
    if (!isValidRedirectPath(path)) {
      console.warn('Stored redirect path is invalid, clearing:', path);
      clearRedirectIntent();
      return null;
    }

    return {
      path,
      action: action || undefined,
    };
  } catch (error) {
    console.error('Failed to retrieve redirect intent:', error);
    return null;
  }
}

/**
 * Clears all stored redirect intent data
 */
export function clearRedirectIntent(): void {
  try {
    sessionStorage.removeItem(REDIRECT_PATH_KEY);
    sessionStorage.removeItem(INTENDED_ACTION_KEY);
    sessionStorage.removeItem(REDIRECT_TIMESTAMP_KEY);
  } catch (error) {
    console.error('Failed to clear redirect intent:', error);
  }
}

/**
 * Gets the redirect path to use after authentication
 * Returns the stored redirect path if valid, otherwise returns the default path
 */
export function getPostAuthRedirectPath(defaultPath: string = '/dashboard'): string {
  const intent = getRedirectIntent();
  
  if (intent) {
    clearRedirectIntent();
    return intent.path;
  }
  
  return defaultPath;
}

// ─── "From" location handling ──────────────────────────────────────────────
/**
 * Paths we must never redirect *to* after login, even if the user came from
 * there — they'd either loop or land on a dead end.
 */
const FROM_PATH_DENYLIST = [
  '/login',
  '/register',
  '/confirm-email',
  '/forgot-password',
  '/reset-password',
  '/complete-oauth-registration',
  '/oauth/callback',
];

/**
 * Relaxed validator for a pathname captured via React Router's
 * `location.state.from` or the last-visited tracker. Unlike
 * `isValidRedirectPath`, this accepts any in-app path that isn't an
 * open-redirect attack vector or an auth route (which would cause a
 * login loop).
 */
export function isSafeFromPath(path: string): boolean {
  if (!path || typeof path !== 'string') return false;
  if (path.startsWith('http://') || path.startsWith('https://') || path.startsWith('//')) return false;
  if (!path.startsWith('/')) return false;
  const base = path.split(/[?#]/)[0];
  if (FROM_PATH_DENYLIST.includes(base)) return false;
  return true;
}

// ─── Last-visited tracker ─────────────────────────────────────────────────
// Separately from the explicit `storeRedirectIntent` flow (which is scoped
// to gated actions like "신청하기"), we also track the last non-auth page
// the user visited. This is what drives the generic "send me back where I
// was" behavior after a plain login, even when the user reached /login
// without going through a React Router `<Link state>` (direct URL, bookmark,
// hard reload, OAuth round-trip, etc.).
const LAST_VISITED_PATH_KEY = 'auth_last_visited_path';
const LAST_VISITED_TIMESTAMP_KEY = 'auth_last_visited_timestamp';
const LAST_VISITED_EXPIRY_MS = 30 * 60 * 1000;

/**
 * Record a pathname as the most recent non-auth page the user visited.
 * Only paths that pass `isSafeFromPath` are stored — auth pages never
 * become the post-login target.
 */
export function recordLastVisitedPath(path: string): void {
  if (!path || !isSafeFromPath(path)) return;
  try {
    sessionStorage.setItem(LAST_VISITED_PATH_KEY, path);
    sessionStorage.setItem(LAST_VISITED_TIMESTAMP_KEY, Date.now().toString());
  } catch {
    /* sessionStorage unavailable — ignore */
  }
}

/**
 * Retrieve the last-visited path if it hasn't expired and still passes
 * `isSafeFromPath`. Returns an empty string otherwise.
 */
export function getLastVisitedPath(): string {
  try {
    const path = sessionStorage.getItem(LAST_VISITED_PATH_KEY);
    const ts = sessionStorage.getItem(LAST_VISITED_TIMESTAMP_KEY);
    if (!path) return '';
    if (ts) {
      const age = Date.now() - parseInt(ts, 10);
      if (age > LAST_VISITED_EXPIRY_MS) {
        sessionStorage.removeItem(LAST_VISITED_PATH_KEY);
        sessionStorage.removeItem(LAST_VISITED_TIMESTAMP_KEY);
        return '';
      }
    }
    return isSafeFromPath(path) ? path : '';
  } catch {
    return '';
  }
}

export function clearLastVisitedPath(): void {
  try {
    sessionStorage.removeItem(LAST_VISITED_PATH_KEY);
    sessionStorage.removeItem(LAST_VISITED_TIMESTAMP_KEY);
  } catch {
    /* ignore */
  }
}
